import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Constants
import { weekDaysOptions } from 'Shared/constants/constants';
import RecurringWorkspaceItemConstants from 'Shared/constants/RecurringWorkspaceItem.constants';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import RecurringWorkspaceItemHelper from 'Shared/model_helpers/RecurringWorkspaceItem.helper';

// Actions
import * as MeetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import RecurrenceEditor from 'Shared/components/modals/RecurringItemModal/RecurrenceEditor';

// Styles
import './RecurrenceModal.scss';

type Props = {
  isOpen: boolean;
  toggleModal(): void;
  meetingItem: Record<MeetingItem>;
};

const RecurrenceModal = ({ isOpen, toggleModal, meetingItem }: Props) => {
  const dispatch = useDispatch();

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const recurringWorkspaceItem = MeetingItemHelpers.getRcurringWorkspaceItem(meetingItem);

  const timeOfDay = RecurringWorkspaceItemHelper.getTimeOfDay(recurringWorkspaceItem);
  const dayOfWeek = RecurringWorkspaceItemHelper.getDayOfWeek(recurringWorkspaceItem);
  const intervalUnit = RecurringWorkspaceItemHelper.getIntervalUnit(recurringWorkspaceItem);
  const intervalAmount = RecurringWorkspaceItemHelper.getIntervalAmount(recurringWorkspaceItem);
  const startsAt = RecurringWorkspaceItemHelper.getStartsAt(recurringWorkspaceItem);
  const dayInMonth = RecurringWorkspaceItemHelper.getDayInMonth(recurringWorkspaceItem);
  const weekInMonth = RecurringWorkspaceItemHelper.getWeekInMonth(recurringWorkspaceItem);

  const [isDeleteing, toggleIsDeleting] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [hasError, setHasError] = useState(false);

  const dayTodayNumber = new Date().getDay();
  const dayToday = weekDaysOptions.find(({ dayNumber }) => dayNumber === dayTodayNumber).value;
  const initialDayValue = dayOfWeek || dayToday;

  const [tempIntervalAmount, setTempIntervalAmount] = useState(intervalAmount || 1);
  const [tempIntervalUnit, setTempIntervalUnit] = useState(intervalUnit || 'week');
  const [tempDayOfWeek, setTempDayOfWeek] = useState(initialDayValue);
  const [tempTimeOfDay, setTempTimeOfDay] = useState(timeOfDay || '09:00');
  const [tempStartsAt, setTempStartsAt] = useState(startsAt ? moment(startsAt) : moment());
  const [tempDayInMonth, setTempDayInMonth] = useState(dayInMonth);
  const [tempWeekInMonth, setTempWeekInMonth] = useState(weekInMonth);

  const handleSaveRecurrenceClick = useCallback(() => {
    toggleIsSaving();
    const data = {
      [RecurringWorkspaceItemConstants.Model.DAY_OF_WEEK]: tempDayOfWeek,
      [RecurringWorkspaceItemConstants.Model.INTERVAL_AMOUNT]: tempIntervalAmount,
      [RecurringWorkspaceItemConstants.Model.INTERVAL_UNIT]: tempIntervalUnit,
      [RecurringWorkspaceItemConstants.Model.TIME_OF_DAY]: tempTimeOfDay,
      [RecurringWorkspaceItemConstants.Model.STARTS_AT]: moment(tempStartsAt).format(),
      [RecurringWorkspaceItemConstants.Model.DAY_IN_MONTH]: tempDayInMonth,
      [RecurringWorkspaceItemConstants.Model.WEEK_IN_MONTH]: tempWeekInMonth,
    };
    dispatch(MeetingItemActions.updateRecurringItem(uuid, data)).then(() => {
      toggleIsSaving();
      toggleModal();
    });
  }, [
    toggleIsSaving,
    tempDayOfWeek,
    tempIntervalAmount,
    tempIntervalUnit,
    tempTimeOfDay,
    tempStartsAt,
    tempDayInMonth,
    tempWeekInMonth,
  ]);

  const handleRemoveClick = useCallback(() => {
    toggleIsDeleting();
    dispatch(MeetingItemActions.deleteRecurrenceItem(uuid)).then(() => {
      toggleIsDeleting();
      toggleModal();
    });
  }, [toggleIsDeleting]);

  const handleSetIntervalAmount = amount => {
    if (!amount) {
      setHasError(true);
    } else {
      setHasError(false);
    }
    setTempIntervalAmount(amount);
  };

  const showDeleteButton = recurringWorkspaceItem;
  const buttonsDisabled = isDeleteing || isSaving;

  if (!isOpen) return null;

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={toggleModal}>
      <WpModalHeader>{recurringWorkspaceItem ? 'Edit Recurrence' : 'Create Recurrence'}</WpModalHeader>
      <WpModalBody>
        Recurring items will reappear according to the recurrence you set below.
        <div className="RecurrenceModal__editor">
          <form>
            <RecurrenceEditor
              days={tempDayOfWeek}
              setDay={setTempDayOfWeek}
              timeOfDay={tempTimeOfDay}
              setTimeOfDay={setTempTimeOfDay}
              intervalUnit={tempIntervalUnit}
              setIntervalUnit={setTempIntervalUnit}
              intervalAmount={tempIntervalAmount}
              setIntervalAmount={handleSetIntervalAmount}
              startsAt={tempStartsAt}
              setStartsAt={setTempStartsAt}
              dayInMonth={tempDayInMonth}
              setDayInMonth={setTempDayInMonth}
              weekInMonth={tempWeekInMonth}
              setWeekInMonth={setTempWeekInMonth}
              hideRecurrenceLabel
            />
          </form>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <div className="RecurrenceModal__footer">
          {showDeleteButton && (
            <WpButton color="danger" onClick={handleRemoveClick} loading={isDeleteing} disabled={buttonsDisabled}>
              Remove recurrence
            </WpButton>
          )}
          <div className="ml-auto">
            <WpButtonGroup>
              <WpButton variant="tertiary" onClick={() => toggleModal()} disabled={buttonsDisabled}>
                Cancel
              </WpButton>
              <WpButton onClick={handleSaveRecurrenceClick} disabled={buttonsDisabled || hasError} loading={isSaving}>
                Save
              </WpButton>
            </WpButtonGroup>
          </div>
        </div>
      </WpModalFooter>
    </WpModal>
  );
};

export default RecurrenceModal;
